/**
 * Notification.jsx — Centre de notifications et de messages
 *
 * Composant polyvalent : s'adapte à trois contextes selon les props.
 *  - bottomBarMode  : deux boutons dans la barre de navigation mobile (Notif + Messages)
 *  - mobileMode     : deux lignes cliquables dans le menu mobile de la navbar
 *  - défaut         : icône cloche + popover dropdown dans la navbar desktop
 *
 * Données chargées en parallèle via Promise.allSettled :
 *  1. /api/discussions  — messages clients (filtrés par role : agencyId ou customerId)
 *  2. /api/payments     — historique des paiements de l'utilisateur
 *  3. /api/users/:id    — statut du compte et modération de la photo de profil
 *
 * Les compteurs "non lus" sont calculés à partir du timestamp stocké en localStorage.
 * Le polling s'effectue toutes les 30 secondes.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FiBell, FiCheckCircle, FiCreditCard, FiInfo, FiMessageCircle, FiUser } from 'react-icons/fi';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import './Notification.css';

const apiBaseUrl = getApiBaseUrl();

function formatRelativeDate(value) {
	if (!value) {
		return '';
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return '';
	}

	return date.toLocaleString('fr-FR', {
		day: '2-digit',
		month: '2-digit',
		hour: '2-digit',
		minute: '2-digit'
	});
}

/** Normalise les champs de l'utilisateur connecté pour un usage uniforme dans ce composant */
function normalizeUser(authUser) {
	if (!authUser) {
		return { id: '', email: '', name: '', role: '' };
	}

	const firstName = String(authUser.firstName || '').trim();
	const lastName = String(authUser.lastName || '').trim();

	return {
		id: String(authUser.id || authUser._id || '').trim(),
		email: String(authUser.email || '').trim().toLowerCase(),
		name: `${firstName} ${lastName}`.trim() || String(authUser.username || authUser.email || 'Utilisateur').trim(),
		role: String(authUser.role || '').trim()
	};
}

function getLastMessage(discussion) {
	const messages = Array.isArray(discussion?.messages) ? discussion.messages : [];
	if (!messages.length) {
		return null;
	}

	return messages[messages.length - 1];
}

function NotificationCenter({ authUser, onNavigate, mobileMode, bottomBarMode }) {
	const navigate = useNavigate();
	const wrapperRef = useRef(null);

	// ── États UI ────────────────────────────────────────────────────────────
	const [isNotifOpen, setIsNotifOpen] = useState(false);       // dropdown desktop ouvert ?
	const [isNotifModalOpen, setIsNotifModalOpen] = useState(false); // modale "Voir tout" ouverte ?
	const [isLoading, setIsLoading] = useState(false);

	// ── Données ──────────────────────────────────────────────────────────────
	const [notifications, setNotifications] = useState([]);
	const [discussions, setDiscussions] = useState([]);

	const user = useMemo(() => normalizeUser(authUser), [authUser]);
	const userStorageSuffix = useMemo(() => user.id || user.email || 'guest', [user.id, user.email]);

	// Clés localStorage par utilisateur — évite les conflits entre comptes sur le même navigateur
	const notifSeenKey = useMemo(() => `ux-notif-read-at:${userStorageSuffix}`, [userStorageSuffix]);
	const messageSeenKey = useMemo(() => `ux-msg-read-at:${userStorageSuffix}`, [userStorageSuffix]);

	// Timestamp (ms) de la dernière ouverture de chaque panneau → sert à calculer les badges "non lu"
	const [lastNotifSeenAt, setLastNotifSeenAt] = useState(() => Number(window.localStorage.getItem(notifSeenKey) || 0));
	const [lastMessageSeenAt, setLastMessageSeenAt] = useState(() => Number(window.localStorage.getItem(messageSeenKey) || 0));

	useEffect(() => {
		setLastNotifSeenAt(Number(window.localStorage.getItem(notifSeenKey) || 0));
	}, [notifSeenKey]);

	useEffect(() => {
		setLastMessageSeenAt(Number(window.localStorage.getItem(messageSeenKey) || 0));
	}, [messageSeenKey]);

	/**
	 * Chargement principal : discussions + paiements + profil en parallèle.
	 * Promise.allSettled garantit que les trois requêtes s'exécutent même si l'une échoue.
	 * isCancelled empêche de mettre à jour l'état après le démontage du composant.
	 * Polling toutes les 30 secondes via setInterval.
	 */
	useEffect(() => {
		if (!authUser) {
			setNotifications([]);
			setDiscussions([]);
			setIsNotifOpen(false);
			return undefined;
		}

		let isCancelled = false;

		const loadCenterData = async () => {
			setIsLoading(true);

			try {
				const isAgencyOrAdmin = user.role === 'agency' || user.role === 'admin';
				const discussionUrl = user.id
					? isAgencyOrAdmin
						? `${apiBaseUrl}/api/discussions?agencyId=${encodeURIComponent(user.id)}`
						: `${apiBaseUrl}/api/discussions?customerId=${encodeURIComponent(user.id)}`
					: `${apiBaseUrl}/api/discussions`;

				const [discussionsRes, paymentsRes, profileRes] = await Promise.allSettled([
					fetch(discussionUrl),
					fetch(`${apiBaseUrl}/api/payments`),
					user.id ? fetch(`${apiBaseUrl}/api/users/${encodeURIComponent(user.id)}`) : Promise.resolve(null)
				]);

				let nextDiscussions = [];
				if (discussionsRes.status === 'fulfilled' && discussionsRes.value?.ok) {
					const data = await discussionsRes.value.json();
					nextDiscussions = Array.isArray(data) ? data : [];
				}

				let payments = [];
				if (paymentsRes.status === 'fulfilled' && paymentsRes.value?.ok) {
					const data = await paymentsRes.value.json();
					payments = Array.isArray(data)
						? data.filter((item) => {
							const sameEmail = user.email && String(item.customerEmail || '').trim().toLowerCase() === user.email;
							const sameName = user.name && String(item.customerName || '').trim().toLowerCase() === user.name.toLowerCase();
							return Boolean(sameEmail || sameName);
						})
						: [];
				}

				let profileData = null;
				if (profileRes.status === 'fulfilled' && profileRes.value?.ok) {
					profileData = await profileRes.value.json();
				}
				const moderationStatus = profileData?.user?.photoModeration || null;
				const accountStatus = profileData?.user?.accountStatus || null;

				const discussionNotifications = nextDiscussions
					.filter((discussion) => {
						const lastMessage = getLastMessage(discussion);
						if (!lastMessage || lastMessage.senderRole === 'system') return false;
						if (lastMessage.senderId && String(lastMessage.senderId) === String(user.id)) return false;
						return true;
					})
					.map((discussion) => {
						const lastMessage = getLastMessage(discussion);
						return {
							id: `d-${discussion._id}`,
							discussionId: discussion._id,
							title: `● Message : ${discussion.vehicleName || discussion.agencyName || 'Annonce'}`,
							description: discussion.lastMessagePreview || lastMessage?.text || 'Nouveau message dans votre discussion.',
							createdAt: discussion.lastMessageAt || discussion.updatedAt,
							type: 'discussion'
						};
					});

				const PAYMENT_STATUS_LABELS = {
					paid: 'Paiement confirmé',
					succeeded: 'Paiement confirmé',
					pending: 'Paiement en attente',
					failed: 'Paiement échoué',
					refunded: 'Paiement remboursé',
					cancelled: 'Paiement annulé',
				};

				const paymentNotifications = payments.slice(0, 6).map((payment) => ({
					id: `p-${payment._id}`,
					paymentId: payment._id,
					title: PAYMENT_STATUS_LABELS[payment.status] || 'Paiement enregistré',
					description: `${Number(payment.amount || 0).toLocaleString('fr-FR')} MAD · ${payment.vehicleName || 'Véhicule'}`,
					createdAt: payment.createdAt || payment.paidAt,
					type: 'payment',
					status: payment.status,
				}));

				const profileNotifications = [];

				if (accountStatus === 'approved') {
					profileNotifications.push({
						id: 'account-approved',
						title: 'Compte validé par l\'admin',
						description: 'Votre compte a été approuvé. Vous pouvez maintenant publier des annonces.',
						createdAt: profileData?.user?.updatedAt,
						type: 'account'
					});
				}

				if (accountStatus === 'rejected' || accountStatus === 'suspended') {
					profileNotifications.push({
						id: 'account-rejected',
						title: 'Compte suspendu',
						description: 'Votre compte a été suspendu. Contactez le support pour plus d\'informations.',
						createdAt: profileData?.user?.updatedAt,
						type: 'account'
					});
				}

				if (moderationStatus?.status === 'pending') {
					profileNotifications.push({
						id: 'profile-photo-pending',
						title: 'Photo de profil en attente',
						description: 'Votre photo est en cours de validation par un administrateur.',
						createdAt: moderationStatus.submittedAt,
						type: 'profile'
					});
				}

				if (moderationStatus?.status === 'rejected') {
					profileNotifications.push({
						id: 'profile-photo-rejected',
						title: 'Photo de profil refusée',
						description: moderationStatus.rejectionReason || 'Votre photo a été refusée. Merci de la remplacer.',
						createdAt: moderationStatus.reviewedAt || moderationStatus.submittedAt,
						type: 'profile'
					});
				}

				const nextNotifications = [...profileNotifications, ...paymentNotifications, ...discussionNotifications]
					.filter((item) => item.title)
					.sort((left, right) => new Date(right.createdAt || 0).getTime() - new Date(left.createdAt || 0).getTime())
					.slice(0, 12);

				if (!isCancelled) {
					setDiscussions(
						nextDiscussions
							.sort((left, right) => new Date(right.lastMessageAt || 0).getTime() - new Date(left.lastMessageAt || 0).getTime())
							.slice(0, 12)
					);
					setNotifications(nextNotifications);
				}
			} catch (error) {
				if (!isCancelled) {
					setDiscussions([]);
					setNotifications([]);
				}
			} finally {
				if (!isCancelled) {
					setIsLoading(false);
				}
			}
		};

		loadCenterData();
		const intervalId = window.setInterval(loadCenterData, 30000);

		return () => {
			isCancelled = true;
			window.clearInterval(intervalId);
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [authUser, user.email, user.id, user.name]);

	useEffect(() => {
		if (!isNotifOpen) {
			return undefined;
		}

		const closeOnOutsideClick = (event) => {
			if (!wrapperRef.current?.contains(event.target)) {
				setIsNotifOpen(false);
			}
		};

		const closeOnEscape = (event) => {
			if (event.key === 'Escape') {
				setIsNotifOpen(false);
			}
		};

		window.addEventListener('mousedown', closeOnOutsideClick);
		window.addEventListener('keydown', closeOnEscape);

		return () => {
			window.removeEventListener('mousedown', closeOnOutsideClick);
			window.removeEventListener('keydown', closeOnEscape);
		};
	}, [isNotifOpen]);

	useEffect(() => {
		if (!isNotifModalOpen) {
			return undefined;
		}

		if (!isNotifOpen) {
			setIsNotifOpen(false);
		}

		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';

		const closeOnOutsideClick = (event) => {
			if (!event.target.closest('.ux-notif-modal-card')) {
				setIsNotifModalOpen(false);
			}
		};

		const closeOnEscape = (event) => {
			if (event.key === 'Escape') {
				setIsNotifModalOpen(false);
			}
		};

		window.addEventListener('mousedown', closeOnOutsideClick);
		window.addEventListener('keydown', closeOnEscape);

		return () => {
			document.body.style.overflow = previousOverflow;
			window.removeEventListener('mousedown', closeOnOutsideClick);
			window.removeEventListener('keydown', closeOnEscape);
		};
	}, [isNotifModalOpen, isNotifOpen]);

	// Conversations avec au moins un message non lu de l'agence/propriétaire
	const unreadMessages = useMemo(() => {
		return discussions.reduce((count, discussion) => {
			const timestamp = new Date(discussion.lastMessageAt || discussion.updatedAt || 0).getTime();
			const lastMessage = getLastMessage(discussion);
			// isMine : le dernier message a été envoyé par l'utilisateur courant → pas "non lu"
			const isMine = user.id && lastMessage?.senderId && String(lastMessage.senderId) === String(user.id);
			const fromAgent = lastMessage?.senderRole === 'agency' || lastMessage?.senderRole === 'owner';
			if (timestamp > lastMessageSeenAt && fromAgent && !isMine) {
				return count + 1;
			}
			return count;
		}, 0);
	}, [discussions, lastMessageSeenAt, user.id]);

	// Notifications dont la date de création est postérieure à la dernière ouverture du panneau
	const unreadNotifications = useMemo(() => {
		return notifications.reduce((count, item) => {
			const timestamp = new Date(item.createdAt || 0).getTime();
			return timestamp > lastNotifSeenAt ? count + 1 : count;
		}, 0);
	}, [notifications, lastNotifSeenAt]);

	const markNotificationsAsRead = () => {
		const now = Date.now();
		window.localStorage.setItem(notifSeenKey, String(now));
		setLastNotifSeenAt(now);
	};

	const markMessagesAsRead = () => {
		const now = Date.now();
		window.localStorage.setItem(messageSeenKey, String(now));
		setLastMessageSeenAt(now);
	};

	const toggleNotifications = () => {
		setIsNotifOpen((previous) => {
			const next = !previous;
			if (next) {
				markNotificationsAsRead();
			}
			return next;
		});
	};

	const handleOpenNotificationsModal = () => {
		setIsNotifOpen(false);
		setIsNotifModalOpen(true);
	};

	const handleOpenDiscussionsPage = () => {
		markMessagesAsRead();
		setIsNotifOpen(false);
		setIsNotifModalOpen(false);
		navigate('/messages');
		if (onNavigate) onNavigate();
	};

	const handleNotifClick = (item) => {
		setIsNotifOpen(false);
		setIsNotifModalOpen(false);
		if (item.type === 'discussion' && item.discussionId) {
			navigate('/messages', { state: { selectDiscussionId: item.discussionId } });
		} else if (item.type === 'payment') {
			navigate('/profile', { state: { tab: 'reservations' } });
		} else if (item.type === 'account' || item.type === 'profile') {
			navigate('/profile');
		}
		if (onNavigate) onNavigate();
	};

	if (!authUser) {
		return null;
	}

	const notificationsModal = isNotifModalOpen
		? createPortal(
			<div className="ux-notif-modal-overlay" role="dialog" aria-modal="true" aria-label="Centre de notifications">
				<div className="ux-notif-modal-card">
					<div className="ux-notif-modal-head">
						<div>
							<h3>Centre de notifications</h3>
							<p>{notifications.length} notification(s)</p>
						</div>
						<div className="ux-notif-modal-actions">
							<button type="button" className="ux-profile-dropdown-item" onClick={() => setIsNotifModalOpen(false)}>
								Fermer
							</button>
						</div>
					</div>

					<div className="ux-notif-modal-list">
						{isLoading ? <p className="ux-nav-popover-empty">Chargement...</p> : null}
						{!isLoading && notifications.length === 0 ? (
							<p className="ux-nav-popover-empty">Aucune notification pour le moment.</p>
						) : null}
						{notifications.map((item) => (
							<button
								key={item.id}
								type="button"
								className="ux-nav-popover-item is-clickable"
								role="listitem"
								onClick={() => handleNotifClick(item)}
							>
								<div className={`ux-nav-popover-item-icon notif-type-${item.type}`} aria-hidden="true">
									{item.type === 'payment' ? <FiCreditCard size={14} /> : item.type === 'discussion' ? <FiMessageCircle size={14} /> : item.type === 'account' || item.type === 'profile' ? <FiUser size={14} /> : <FiInfo size={14} />}
								</div>
								<div>
									<strong>{item.title}</strong>
									<p>{item.description}</p>
									<small>{formatRelativeDate(item.createdAt)}</small>
								</div>
							</button>
						))}
					</div>
				</div>
			</div>,
			document.body
		)
		: null;

	if (bottomBarMode) {
		return (
			<div style={{ display: 'contents' }}>
				<button
					type="button"
					className="vx-bottom-nav-item vx-bn-notif"
					onClick={handleOpenNotificationsModal}
					aria-label="Notifications"
				>
					<span className="vx-bottom-nav-icon-wrap">
						<FiBell size={22} aria-hidden="true" />
						{unreadNotifications > 0 ? (
							<span className="vx-bottom-nav-badge">{Math.min(unreadNotifications, 99)}</span>
						) : null}
					</span>
					<span>Notif</span>
				</button>
				<button
					type="button"
					className="vx-bottom-nav-item vx-bn-msg"
					onClick={handleOpenDiscussionsPage}
					aria-label="Messages"
				>
					<span className="vx-bottom-nav-icon-wrap">
						<FiMessageCircle size={22} aria-hidden="true" />
						{unreadMessages > 0 ? (
							<span className="vx-bottom-nav-badge">{Math.min(unreadMessages, 99)}</span>
						) : null}
					</span>
					<span>Messages</span>
				</button>
				{notificationsModal}
			</div>
		);
	}

	if (mobileMode) {
		return (
			<div className="ux-notification-center ux-notif-mobile-bar" ref={wrapperRef}>
				<button
					type="button"
					className="ux-notif-mobile-row"
					onClick={handleOpenNotificationsModal}
					aria-label="Voir les notifications"
				>
					<FiBell size={16} aria-hidden="true" />
					<span>Notifications</span>
					{unreadNotifications > 0 ? (
						<span className="ux-notif-mobile-badge">{Math.min(unreadNotifications, 99)}</span>
					) : null}
				</button>
				<button
					type="button"
					className="ux-notif-mobile-row"
					onClick={handleOpenDiscussionsPage}
					aria-label="Voir les messages"
				>
					<FiMessageCircle size={16} aria-hidden="true" />
					<span>Messages</span>
					{unreadMessages > 0 ? (
						<span className="ux-notif-mobile-badge">{Math.min(unreadMessages, 99)}</span>
					) : null}
				</button>
				{notificationsModal}
			</div>
		);
	}

	const previewNotifications = notifications.slice(0, 4);

	return (
		<div className="ux-notification-center" ref={wrapperRef}>
			<div className={`ux-nav-popover ${isNotifOpen ? 'is-open' : ''}`}>
				<button
					type="button"
					className="ux-nav-icon-btn"
					onClick={toggleNotifications}
					aria-expanded={isNotifOpen}
					aria-haspopup="menu"
					aria-label="Ouvrir les notifications"
				>
					<FiBell size={16} />
					{unreadNotifications > 0 ? <span className="ux-nav-icon-count">{Math.min(unreadNotifications, 99)}</span> : null}
				</button>

				<div className="ux-nav-popover-menu" role="menu" aria-label="Notifications">
					<div className="ux-nav-popover-head">
						<strong>Notifications</strong>
						<span>{notifications.length}</span>
					</div>
					<div className="ux-nav-popover-list">
						{isLoading ? <p className="ux-nav-popover-empty">Chargement...</p> : null}
						{!isLoading && notifications.length === 0 ? (
							<p className="ux-nav-popover-empty">Aucune notification pour le moment.</p>
						) : null}
						{previewNotifications.map((item) => (
							<button
								key={item.id}
								type="button"
								className="ux-nav-popover-item is-clickable"
								role="menuitem"
								onClick={() => handleNotifClick(item)}
							>
								<div className={`ux-nav-popover-item-icon notif-type-${item.type}`} aria-hidden="true">
									{item.type === 'payment' ? <FiCreditCard size={14} /> : item.type === 'discussion' ? <FiMessageCircle size={14} /> : item.type === 'account' || item.type === 'profile' ? <FiUser size={14} /> : <FiInfo size={14} />}
								</div>
								<div>
									<strong>{item.title}</strong>
									<p>{item.description}</p>
									<small>{formatRelativeDate(item.createdAt)}</small>
								</div>
							</button>
						))}
					</div>
					<button type="button" className="ux-notif-see-all-btn" onClick={handleOpenNotificationsModal}>
						Voir tout
					</button>
				</div>
			</div>

			{notificationsModal}

			<button
				type="button"
				className="ux-nav-icon-btn"
				onClick={handleOpenDiscussionsPage}
				aria-label="Ouvrir toutes les discussions"
				title="Mes discussions"
			>
				<FiMessageCircle size={16} />
				{unreadMessages > 0 ? <span className="ux-nav-icon-count">{Math.min(unreadMessages, 99)}</span> : null}
			</button>
		</div>
	);
}

NotificationCenter.propTypes = {
	authUser: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		email: PropTypes.string,
		firstName: PropTypes.string,
		lastName: PropTypes.string,
		username: PropTypes.string
	}),
	onNavigate: PropTypes.func,
	mobileMode: PropTypes.bool,
	bottomBarMode: PropTypes.bool
};

NotificationCenter.defaultProps = {
	authUser: null,
	onNavigate: null,
	mobileMode: false,
	bottomBarMode: false
};

export default NotificationCenter;
